import { KeyboardEvent, useState } from 'react';

import { getItemPopupList } from 'src/apis/itemApi';
import { searchCustomer } from 'src/apis/customerApi';
import { SearchCodeModalForm } from 'src/components/common/form/search/SearchCodeModalForm';
import { SearchDropDownForm } from 'src/components/common/form/search/SearchDropDownForm';
import { SearchDateRangeForm } from 'src/components/common/form/search/SearchDateRangeForm';
import { SysCodeDto } from 'src/dtos/code/sysCodeDto';
import { useInfoFormEvents } from 'src/hooks/useInfoFormEvents';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import useUiuxStore from 'src/store/useUiuxStore';
import { InfoCodeSearchFieldConfig } from 'src/types/info-form';
import { SearchSectionDoubleForm } from 'src/components/common/form/search/SearchSectionDoubleForm';
import { defaultTQC1020_R01Filter, useTQC1020_R01Store } from './Store_TQC1020_R01';

interface Props {
    onEnterKeyDown: () => void;
}

const INOUT_CLASS_TARGETS = ['110', '170'];

export const SearchSection_TQC1020_R01 = ({ onEnterKeyDown }: Props) => {
    const { TQC1020_R01Filter, setTQC1020_R01Filter } = useTQC1020_R01Store();
    const { compCdList, inoutClassList } = useCommonCodeStore();
    const { setCodeSearchModalProps } = useUiuxStore();

    const [pickerVisible, setPickerVisible] = useState(false);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });

    const handleCustFoucsId = 'tqc1020_customCd_search';
    const handleItemFoucsId = 'tqc1020_itemCd_search';
    const inoutClassOptions = inoutClassList.filter((code) =>
        INOUT_CLASS_TARGETS.includes(code.minorCd)
    );

    const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
        {
            fieldName: 'customCd',
            category: '거래처',
            focusId: handleCustFoucsId,
            responseMapping: {
                customCd: 'customCd',
                customNm: 'customNm',
            },
            searchFunction: async (searchText: string) => {
                const customerResponse = await searchCustomer(searchText);
                return customerResponse.data.data.find((value) => value.customCd === searchText);
            },
            extraFilter: () => ({
                customCd: TQC1020_R01Filter.customCd || '',
                customNm: TQC1020_R01Filter.customNm || '',
            }),
        },
        {
            fieldName: 'itemCd',
            category: '품목',
            focusId: handleItemFoucsId,
            responseMapping: {
                itemCd: 'itemCd',
                itemNm: 'itemNm',
            },
            searchFunction: async (searchText: string) => {
                const itemResponse = await getItemPopupList(searchText, '', '', '');
                return itemResponse.data.data.find((value) => value.itemCd === searchText);
            },
            extraFilter: () => ({
                itemCd: TQC1020_R01Filter.itemCd || '',
                itemNm: TQC1020_R01Filter.itemNm || '',
            }),
        },
    ];

    const { getFieldHandlers } = useInfoFormEvents({
        editData: TQC1020_R01Filter,
        setEditData: setTQC1020_R01Filter,
        defaultData: defaultTQC1020_R01Filter,
        infoFieldConfigs,
        setCodeSearchModalProps,
        formName: 'TQC1020_R01_search',
    });

    const handleCustomKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
        getFieldHandlers('customCd').onEnterKeyDown(e);
        if (e.key === 'Enter') {
            onEnterKeyDown();
        }
    };

    const handleItemKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
        getFieldHandlers('itemCd').onEnterKeyDown(e);
        if (e.key === 'Enter') {
            onEnterKeyDown();
        }
    };

    return (
        <SearchSectionDoubleForm>
            <SearchDropDownForm
                label="사업장"
                value={TQC1020_R01Filter.compCd}
                optionList={compCdList}
                onChangeItem={(selectedItem) =>
                    setTQC1020_R01Filter({ compCd: selectedItem?.compCd || '' })
                }
            />

            <SearchDateRangeForm
                label="입고일자"
                visible={pickerVisible}
                setVisible={setPickerVisible}
                dateRange={TQC1020_R01Filter.transportDt}
                setDateRange={(range) => setTQC1020_R01Filter({ transportDt: range })}
                pickerPosition={pickerPosition}
                setPickerPosition={setPickerPosition}
            />

            <SearchDropDownForm<SysCodeDto>
                label="입고구분"
                value={TQC1020_R01Filter.inoutClass}
                optionList={inoutClassOptions}
                onChangeItem={(selectedItem) =>
                    setTQC1020_R01Filter({ inoutClass: selectedItem?.minorCd || '' })
                }
            />

            <SearchCodeModalForm
                label="거래처"
                value={TQC1020_R01Filter.customCd}
                valueLabel={TQC1020_R01Filter.customNm}
                handleFoucsId={handleCustFoucsId}
                onChange={getFieldHandlers('customCd').onChange}
                onDoubleClick={getFieldHandlers('customCd').onDoubleClick}
                onKeyDown={handleCustomKeyDown}
            />

            <SearchCodeModalForm
                label="품목코드"
                value={TQC1020_R01Filter.itemCd}
                valueLabel={TQC1020_R01Filter.itemNm}
                handleFoucsId={handleItemFoucsId}
                onChange={getFieldHandlers('itemCd').onChange}
                onDoubleClick={getFieldHandlers('itemCd').onDoubleClick}
                onKeyDown={handleItemKeyDown}
            />

            <div className="flex items-center gap-2">
                <label className="h-[24px] pl-4 flex items-center gap-[24px] truncate cursor-pointer">
                    검사완료포함
                </label>
                <input
                    type="checkbox"
                    checked={TQC1020_R01Filter.includeCompleted}
                    onChange={(e) =>
                        setTQC1020_R01Filter({ includeCompleted: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
            </div>
        </SearchSectionDoubleForm>
    );
};
